import type { StateAction } from "@entrilia/shared/types";
import { arraysHaveSameItems, distinct, intersect } from "@entrilia/shared/utilities/arrayHelper";
import { isDefined } from "@entrilia/shared/utilities/typeHelper";
import type {
  FundInvestorContact,
  InvestorPortalRole,
  InvestorPortalRoleWithNoneValue,
} from "../../../../api/types/investorContactsTypes";
import type {
  MemberChangeItem,
  MemberFundPermissions,
  MembersChangeRequestDeltaRequest,
  MembersChangeRequestView,
  UpsertMemberChangeItem,
  UpsertMembersChangeRequestRequest,
} from "../../../../api/types/membersChangeRequestTypes";
import type { InvestorFundPermissionSet } from "../../../../store/state/user/types";
import { getBaseChangeRequest, resolveSubmittedComment } from "./membersChangeRequestHelper";

export interface NewContactFund {
  fundId: string;
  fundName: string;
  fundInvestorId: string;
}

export interface NewContactChange {
  contactId: string;
  name: string;
  email: string;
  role: InvestorPortalRole;
  categoryExternalIds: string[];
  funds: NewContactFund[];
}

export interface ContactFundUpdate {
  contactId: string;
  fundId: string;
  role: InvestorPortalRoleWithNoneValue;
  categoryExternalIds: string[];
}

interface ContactFundRemoval {
  contactId: string;
  fundId: string;
}

// The API represents no portal access as an absent role, while updates carry it as an explicit "None"
const updateRoleToApiRole = (role: InvestorPortalRoleWithNoneValue): InvestorPortalRole | undefined =>
  role === "None" ? undefined : role;

export interface ContactChangesState {
  newContacts: NewContactChange[];
  removedContactIds: string[];
  // A pending request can remove a member from some funds while another change item updates them in others, so its removals are per fund
  removedContactFunds: ContactFundRemoval[];
  contactUpdates: ContactFundUpdate[];
  revertedChangeItemIds: string[];
  // Set only by the user; the pending request's comment is derived on read, never copied in here
  comment?: string;
}

export const getInitialContactChangesState = (): ContactChangesState => ({
  newContacts: [],
  removedContactIds: [],
  removedContactFunds: [],
  contactUpdates: [],
  revertedChangeItemIds: [],
});

export interface ContactRemovals {
  removedContactIds: string[];
  removedContactFunds: ContactFundRemoval[];
}

export const isContactFundRemoved = (removals: ContactRemovals, contactId: string, fundId: string): boolean =>
  removals.removedContactIds.includes(contactId) ||
  removals.removedContactFunds.some((removal) => removal.contactId === contactId && removal.fundId === fundId);

export const isContactBeingRemoved = (removals: ContactRemovals, contactId: string): boolean =>
  removals.removedContactIds.includes(contactId) ||
  removals.removedContactFunds.some((removal) => removal.contactId === contactId);

export const getUnsavedChangesCount = (state: ContactChangesState): number =>
  state.newContacts.length +
  state.removedContactIds.length +
  state.contactUpdates.length +
  state.revertedChangeItemIds.length;

export const hasUnsavedChanges = (state: ContactChangesState): boolean => getUnsavedChangesCount(state) > 0;

type FundPermissionTokens = Pick<MemberFundPermissions, "unitId" | "unitRevision">;

// Keyed by the pending change item's contact/operation identity, so an unrelated edit keeps a fund's existing unit token
const buildFundPermissionTokens = (
  changeRequest: MembersChangeRequestView | undefined
): Map<string, FundPermissionTokens> => {
  const tokens = new Map<string, FundPermissionTokens>();
  changeRequest?.items.forEach((item) => {
    const key = item.contactId ?? item.changeItemId;
    if (key === undefined) {
      return;
    }

    item.fundPermissions.forEach((permission) => {
      if (permission.unitId === undefined && permission.unitRevision === undefined) {
        return;
      }

      tokens.set(`${key}::${permission.fundInvestorId}`, {
        unitId: permission.unitId,
        unitRevision: permission.unitRevision,
      });
    });
  });
  return tokens;
};

const withUnitTokens = (
  permission: MemberFundPermissions,
  fundPermissionTokens: Map<string, FundPermissionTokens>,
  key: string
): MemberFundPermissions => ({ ...permission, ...fundPermissionTokens.get(`${key}::${permission.fundInvestorId}`) });

const newContactToUpsertItem = (
  contact: NewContactChange,
  fundPermissionTokens: Map<string, FundPermissionTokens>
): UpsertMemberChangeItem => ({
  action: "CreateNew",
  name: contact.name,
  email: contact.email,
  fundPermissions: contact.funds.map((fund) =>
    withUnitTokens(
      { fundInvestorId: fund.fundInvestorId, role: contact.role, categories: contact.categoryExternalIds },
      fundPermissionTokens,
      contact.contactId
    )
  ),
});

// The backend diffs the item's full permission set, so send every fund's permissions, not only edited ones.
// Except funds a pending removal covers: naming one would adopt the removal's operation and convert it into an update.
// And except placeholder funds without an update: restating a non-membership would stage an empty attach.
const contactUpdatesToUpsertItems = (
  contactUpdates: ContactFundUpdate[],
  contacts: FundInvestorContact[],
  fundInvestors: InvestorFundPermissionSet[],
  fundPermissionTokens: Map<string, FundPermissionTokens>,
  removedContactFunds: ContactFundRemoval[]
): UpsertMemberChangeItem[] =>
  distinct(contactUpdates.map((update) => update.contactId)).map((contactId) => {
    const contactRows = contacts.filter(
      (row) =>
        row.contactId === contactId &&
        (row.isMember || contactUpdates.some((u) => u.contactId === contactId && u.fundId === row.fundId)) &&
        !removedContactFunds.some((removal) => removal.contactId === contactId && removal.fundId === row.fundId)
    );
    const fundPermissions = contactRows
      .map<MemberFundPermissions | undefined>((row) => {
        const fundInvestorId = fundInvestors.find((fundInvestor) => fundInvestor.fundId === row.fundId)?.fundInvestorId;
        if (fundInvestorId === undefined) {
          return undefined;
        }

        const update = contactUpdates.find((u) => u.contactId === contactId && u.fundId === row.fundId);
        return withUnitTokens(
          {
            fundInvestorId,
            role: update ? updateRoleToApiRole(update.role) : row.role,
            categories: update ? update.categoryExternalIds : row.categoryExternalIds,
          },
          fundPermissionTokens,
          contactId
        );
      })
      .filter(isDefined());

    return {
      action: "UpdateExisting",
      contactId,
      name: contactRows[0]?.contactName,
      email: contactRows[0]?.contactEmail,
      fundPermissions,
    };
  });

export type PendingContactChanges = Pick<
  ContactChangesState,
  "newContacts" | "removedContactIds" | "contactUpdates" | "revertedChangeItemIds"
>;

// Removals identify the contact only: the backend snapshots the contact's current permissions itself
export const changesToDeltaRequest = (
  changes: PendingContactChanges,
  contacts: FundInvestorContact[],
  fundInvestors: InvestorFundPermissionSet[],
  changeRequest?: MembersChangeRequestView
): MembersChangeRequestDeltaRequest => {
  // The backend replaces a contact's whole permission set on upsert, so amend the change-request state, not the stored contact
  const pendingChanges = changeRequestToContactChanges(changeRequest, fundInvestors, changes.revertedChangeItemIds);
  const changeRequestContacts = applyContactUpdatesToRows(contacts, pendingChanges.contactUpdates);
  const fundPermissionTokens = buildFundPermissionTokens(changeRequest);
  const upsertItems = [
    ...changes.newContacts.map((contact) => newContactToUpsertItem(contact, fundPermissionTokens)),
    ...contactUpdatesToUpsertItems(
      changes.contactUpdates,
      changeRequestContacts,
      fundInvestors,
      fundPermissionTokens,
      pendingChanges.removedContactFunds
    ),
    ...changes.removedContactIds.map<UpsertMemberChangeItem>((contactId) => ({
      action: "Delete" as const,
      contactId,
      fundPermissions: [],
    })),
  ];

  // The backend rejects touching the same change item twice, so an upsert on a contact supersedes reverting its change item
  const upsertedContactIds = new Set(upsertItems.map((item) => item.contactId).filter(isDefined()));
  const removeChangeItemIds = changes.revertedChangeItemIds.filter((changeItemId) => {
    const contactId = changeRequest?.items.find((item) => item.changeItemId === changeItemId)?.contactId;
    return contactId === undefined || !upsertedContactIds.has(contactId);
  });

  return { upsertItems, removeChangeItemIds: removeChangeItemIds.length > 0 ? removeChangeItemIds : undefined };
};

export const stateToUpsertMembersChangeRequestRequest = (
  state: ContactChangesState,
  latestChangeRequest: MembersChangeRequestView | undefined,
  contacts: FundInvestorContact[],
  fundInvestors: InvestorFundPermissionSet[],
  submissionId: string
): UpsertMembersChangeRequestRequest => ({
  ...getBaseChangeRequest(latestChangeRequest),
  ...changesToDeltaRequest(state, contacts, fundInvestors, latestChangeRequest),
  commentUserInput: resolveSubmittedComment(state.comment, latestChangeRequest),
  submissionId,
});

export const newContactsToContactRows = (newContacts: NewContactChange[]): FundInvestorContact[] =>
  newContacts.flatMap((contact) =>
    contact.funds.map((fund) => ({
      id: `${contact.contactId}_${fund.fundId}`,
      contactId: contact.contactId,
      isCurrentUser: false,
      isMember: true,
      contactName: contact.name,
      contactEmail: contact.email,
      fundId: fund.fundId,
      fundName: fund.fundName,
      role: contact.role,
      categoryExternalIds: contact.categoryExternalIds,
    }))
  );

export const applyContactUpdatesToRows = (
  contacts: FundInvestorContact[],
  contactUpdates: ContactFundUpdate[]
): FundInvestorContact[] =>
  contacts.map((row) => {
    const update = contactUpdates.find((u) => u.contactId === row.contactId && u.fundId === row.fundId);
    return update
      ? { ...row, role: updateRoleToApiRole(update.role), categoryExternalIds: update.categoryExternalIds }
      : row;
  });

export interface ContactRowUpdateHighlight {
  roleChanged: boolean;
  changedCategoryExternalIds: string[];
}

export const getContactUpdateHighlights = (
  contacts: FundInvestorContact[],
  contactUpdates: ContactFundUpdate[]
): Map<string, ContactRowUpdateHighlight> =>
  new Map(
    contactUpdates
      .map<[string, ContactRowUpdateHighlight] | undefined>((update) => {
        const row = contacts.find((r) => r.contactId === update.contactId && r.fundId === update.fundId);
        if (!row) {
          return undefined;
        }

        return [
          row.id,
          {
            roleChanged: update.role !== (row.role ?? "None"),
            changedCategoryExternalIds: [
              ...update.categoryExternalIds.filter((id) => !row.categoryExternalIds.includes(id)),
              ...row.categoryExternalIds.filter((id) => !update.categoryExternalIds.includes(id)),
            ],
          },
        ];
      })
      .filter(isDefined())
  );

// Derive the ContactChangesState shape from the pending change request so it renders like unsaved changes
const getNewContactRole = (fundPermissions: MemberFundPermissions[]): InvestorPortalRole =>
  fundPermissions.some((permission) => permission.role === "Admin") ? "Admin" : "Member";

const findFund = (
  fundInvestorId: string,
  fundInvestors: InvestorFundPermissionSet[]
): { fundId: string; fundName: string } | undefined => {
  const fundInvestor = fundInvestors.find((investor) => investor.fundInvestorId === fundInvestorId);
  return fundInvestor ? { fundId: fundInvestor.fundId, fundName: fundInvestor.fundName } : undefined;
};

const changeItemToNewContact = (
  item: MemberChangeItem,
  index: number,
  fundInvestors: InvestorFundPermissionSet[]
): NewContactChange => ({
  contactId: item.changeItemId ?? `cr-new-${index}`,
  name: item.name ?? "",
  email: item.email ?? "",
  role: getNewContactRole(item.fundPermissions),
  categoryExternalIds: distinct(item.fundPermissions.flatMap((permission) => permission.categories)),
  funds: item.fundPermissions
    .map<NewContactFund | undefined>((permission) => {
      const fund = findFund(permission.fundInvestorId, fundInvestors);
      return fund ? { ...fund, fundInvestorId: permission.fundInvestorId } : undefined;
    })
    .filter(isDefined()),
});

const fundPermissionChanged = (permission: MemberFundPermissions, oldPermissions: MemberFundPermissions[]): boolean => {
  const previous = oldPermissions.find((old) => old.fundInvestorId === permission.fundInvestorId);
  return (
    !previous || permission.role !== previous.role || !arraysHaveSameItems(permission.categories, previous.categories)
  );
};

const changeItemToContactFundRemovals = (
  item: MemberChangeItem,
  fundInvestors: InvestorFundPermissionSet[]
): ContactFundRemoval[] => {
  const { contactId } = item;
  if (!contactId) {
    return [];
  }

  // A detached unit keeps only its base permissions, so the funds a removal covers are the ones it lists as old
  return item.oldFundPermissions
    .map<ContactFundRemoval | undefined>((permission) => {
      const fund = findFund(permission.fundInvestorId, fundInvestors);
      return fund ? { contactId, fundId: fund.fundId } : undefined;
    })
    .filter(isDefined());
};

const changeItemToContactUpdates = (
  item: MemberChangeItem,
  fundInvestors: InvestorFundPermissionSet[]
): ContactFundUpdate[] => {
  const { contactId } = item;
  if (!contactId) {
    return [];
  }

  return item.fundPermissions
    .filter((permission) => fundPermissionChanged(permission, item.oldFundPermissions))
    .map<ContactFundUpdate | undefined>((permission) => {
      const fund = findFund(permission.fundInvestorId, fundInvestors);
      return fund
        ? {
            contactId,
            fundId: fund.fundId,
            role: permission.role ?? "None",
            categoryExternalIds: permission.categories,
          }
        : undefined;
    })
    .filter(isDefined());
};

export const changeRequestToContactChanges = (
  changeRequest: MembersChangeRequestView | undefined,
  fundInvestors: InvestorFundPermissionSet[],
  revertedChangeItemIds: string[] = []
): ContactChangesState => {
  if (!changeRequest?.changeRequestId) {
    return getInitialContactChangesState();
  }

  const items = changeRequest.items.filter(
    (item) => item.changeItemId === undefined || !revertedChangeItemIds.includes(item.changeItemId)
  );
  return {
    newContacts: items
      .map((item, index) =>
        item.action === "CreateNew" ? changeItemToNewContact(item, index, fundInvestors) : undefined
      )
      .filter(isDefined()),
    // Scoped to the funds each removal actually covers, never the whole contact: a sibling change item may be updating them elsewhere
    removedContactIds: [],
    removedContactFunds: items
      .filter((item) => item.action === "Delete")
      .flatMap((item) => changeItemToContactFundRemovals(item, fundInvestors)),
    contactUpdates: items
      .filter((item) => item.action === "UpdateExisting")
      .flatMap((item) => changeItemToContactUpdates(item, fundInvestors)),
    revertedChangeItemIds: [],
  };
};

// Drops ids whose change item the request no longer holds, so a withdrawal (by anyone, or on a fresh load) can't leave a phantom revert behind
export const reconcileRevertedChangeItemIds = (
  revertedChangeItemIds: string[],
  changeRequest: MembersChangeRequestView | undefined
): string[] => {
  if (!changeRequest?.changeRequestId) {
    return [];
  }

  const liveChangeItemIds = changeRequest.items.map((item) => item.changeItemId).filter(isDefined());
  return intersect(revertedChangeItemIds, liveChangeItemIds);
};

// Overlay (unsaved changes) wins over base (the change request) on the same contact fund
export const mergeContactChanges = (
  base: ContactChangesState,
  overlay: Omit<ContactChangesState, "comment">
): ContactChangesState => {
  const removedContactIds = distinct([...base.removedContactIds, ...overlay.removedContactIds]);
  const removedContactFunds = [...base.removedContactFunds, ...overlay.removedContactFunds];

  const otherBaseContactUpdates = base.contactUpdates.filter(
    (update) =>
      !overlay.contactUpdates.some((other) => other.contactId === update.contactId && other.fundId === update.fundId)
  );

  // A removal only supersedes the updates on the funds it covers, so a member removed from one fund keeps a pending update in another
  const contactUpdates = [...overlay.contactUpdates, ...otherBaseContactUpdates].filter(
    (update) =>
      !removedContactIds.includes(update.contactId) &&
      !removedContactFunds.some((removal) => removal.contactId === update.contactId && removal.fundId === update.fundId)
  );

  return {
    newContacts: [...base.newContacts, ...overlay.newContacts],
    removedContactIds,
    removedContactFunds,
    contactUpdates,
    revertedChangeItemIds: distinct([...base.revertedChangeItemIds, ...overlay.revertedChangeItemIds]),
    comment: base.comment,
  };
};

// Actions

export const removeContactAction =
  (contactId: string): StateAction<ContactChangesState> =>
  (state) => {
    if (state.newContacts.some((contact) => contact.contactId === contactId)) {
      return { ...state, newContacts: state.newContacts.filter((contact) => contact.contactId !== contactId) };
    }
    // The backend rejects a request that both updates and deletes the same contact, so removal cancels pending edits
    return {
      ...state,
      removedContactIds: distinct([...state.removedContactIds, contactId]),
      contactUpdates: state.contactUpdates.filter((update) => update.contactId !== contactId),
    };
  };

export const restoreContactAction =
  (contactId: string): StateAction<ContactChangesState> =>
  (state) => ({ ...state, removedContactIds: state.removedContactIds.filter((id) => id !== contactId) });

export const revertChangeRequestChangeAction =
  (changeItemId: string): StateAction<ContactChangesState> =>
  (state) => ({ ...state, revertedChangeItemIds: distinct([...state.revertedChangeItemIds, changeItemId]) });

export const cancelRevertChangeRequestChangeAction =
  (changeItemId: string): StateAction<ContactChangesState> =>
  (state) => ({ ...state, revertedChangeItemIds: state.revertedChangeItemIds.filter((id) => id !== changeItemId) });

export const reconcileRevertedChangeItemIdsAction =
  (changeRequest: MembersChangeRequestView | undefined): StateAction<ContactChangesState> =>
  (state) => {
    const revertedChangeItemIds = reconcileRevertedChangeItemIds(state.revertedChangeItemIds, changeRequest);
    return revertedChangeItemIds.length === state.revertedChangeItemIds.length
      ? state
      : { ...state, revertedChangeItemIds };
  };

export const applyContactFundUpdateAction =
  (
    update: ContactFundUpdate,
    original: Pick<FundInvestorContact, "role" | "categoryExternalIds">
  ): StateAction<ContactChangesState> =>
  (state) => {
    const otherUpdates = state.contactUpdates.filter(
      (existing) => existing.contactId !== update.contactId || existing.fundId !== update.fundId
    );
    const revertsToOriginal =
      update.role === (original.role ?? "None") &&
      arraysHaveSameItems(update.categoryExternalIds, original.categoryExternalIds);
    return { ...state, contactUpdates: revertsToOriginal ? otherUpdates : [...otherUpdates, update] };
  };

const isSameEmail = (email1: string, email2: string): boolean => email1.toLowerCase() === email2.toLowerCase();

export const addNewContactsAction =
  (newContacts: NewContactChange[], comment: string | undefined): StateAction<ContactChangesState> =>
  (state) => ({
    ...state,
    newContacts: [
      ...state.newContacts.filter((existing) => !newContacts.some((added) => isSameEmail(added.email, existing.email))),
      ...newContacts,
    ],
    comment: comment === undefined ? state.comment : comment,
  });

export const updateCommentAction =
  (value: string): StateAction<ContactChangesState> =>
  (state) => ({ ...state, comment: value });

export const discardChangesAction = (): StateAction<ContactChangesState> => () => getInitialContactChangesState();
